import React, { useCallback, useEffect } from 'react';

import { CircleCrossIcon } from '@vlabs/icons';
import { Page, Divider, Panel, Table, TableCells, openConfirmPopup, Control, useModal } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory, generatePath } from 'react-router-dom';

import { lunaStreamsClient } from 'api';
import { actionColumnProps } from 'helpers';
import * as pt from 'propTypes';
import { routes } from 'routes';
import { requiredValidator } from 'validators';

import { selectStreamOptions } from '../streams/selectors';
import st from './GroupListPage.module.sass';
import { selectGroups, selectPageData, selectPageParameters } from './selectors';
import * as store from './store';

function AttachStreamsModalComponent({
  onSubmit,
  streams,
  group_name,
}) {
  const form = useForm({
    defaultValues: { stream_ids: [] },
  });
  const { t } = useTranslation();

  return (
    <Page className={st.AttachStreamsToGroupModal} title={t('lunaStreamsGroups:attach streams modal.title', { group_name })}>
      <Divider small />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Control.Select
          control={form.control}
          errors={form.formState.errors}
          id="stream_ids"
          isClearable
          isMulti
          label={t('lunaStreamsGroups:attach streams modal.fields.stream_ids.label')}
          name="stream_ids"
          options={streams}
          rules={{ required: requiredValidator() }}
        />
        <Divider small />
        <Control.Button fullWidth type="submit">{t('lunaStreamsGroups:attach streams modal.submit')}</Control.Button>
      </form>
    </Page>
  );
}

AttachStreamsModalComponent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  streams: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  group_name: PropTypes.string.isRequired,
};

AttachStreamsModalComponent.defaultProps = {
  streams: [],
};

const AttachStreamsModal = connect((state) => ({
  streams: selectStreamOptions(state),
}))(AttachStreamsModalComponent);

function GroupStreamsCell({ value }) {
  const modal = useModal();
  const { t } = useTranslation();

  const attachToGroup = useCallback(async ({ stream_ids }) => {
    await lunaStreamsClient.group(value.group_id).attachStreams(stream_ids.map(({ streamId }) => streamId));
    value.refetch();
    modal.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const detachFromGroup = useCallback(async (stream_id) => {
    await lunaStreamsClient.group(value.group_id).detachStreams([stream_id]);
    value.refetch();
  }, [value]);

  return (
    <div className={st.GroupStreamsCell}>
      {modal.wrap(<AttachStreamsModal group_name={value.group_name} onSubmit={attachToGroup} />)}
      <div className={st.GroupStreamsCellControls}>
        <Control.RoundButton
          kind="positive"
          onClick={modal.open}
          title={t('lunaStreamsGroups:attach streams modal.title', { group_name: value.group_name })}
          type="button"
          variant="flat"
        >
          +
        </Control.RoundButton>
      </div>
      <div className={st.GroupStreamsCellStreams}>
        {value?.streams?.map(({ stream_id, name }) => (
          <Control.Button
            icon={<CircleCrossIcon />}
            key={stream_id}
            kind="negative"
            onClick={() => detachFromGroup(stream_id)}
            title={t('lunaStreamsGroups:таблица.подтверждение.открепить.title', { stream_name: name, group_name: value.group_name })}
            type="button"
            variant="flat"
          >
            {name}
          </Control.Button>
        ))}
      </div>
    </div>
  );
}

GroupStreamsCell.propTypes = {
  value: PropTypes.shape({
    group_id: PropTypes.string,
    group_name: PropTypes.string,
    streams: PropTypes.arrayOf(PropTypes.shape({
      stream_id: PropTypes.string,
      name: PropTypes.string,
    })),
    refetch: PropTypes.func.isRequired,
  }),
};
GroupStreamsCell.defaultProps = {
  value: {},
};

function GroupListPage({
  pageData,
  pageParameters,
  setPageIndex,
  setPageSize,
  deleteGroup,
  fetchGroups,
  refetch,
}) {
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const onEditRowHandler = ({ group_id }) => history.push(`/groups/${group_id}`);
  const onDeleteRowHandler = (group) => {
    const message = <span>{t('lunaStreamsGroups:таблица.подтверждение.удаление.текст', { group })}</span>;

    openConfirmPopup(
      {
        title: t('lunaStreamsGroups:таблица.подтверждение.удаление.заголовок'),
        message,
        type: 'delete',
        onConfirm: () => deleteGroup(group.group_id) },
    );
  };

  const actions = (
    <Control.Button onClick={() => history.push(generatePath(routes.sources.lunaStreams.groups.create))}>
      {t('lunaStreamsGroups:таблица.добавить')}
    </Control.Button>
  );

  return (
    <Page actions={actions} title={t('lunaStreamsGroups:название раздела')}>
      <Divider />
      <Panel>
        <Table
          {...pageData}
          {...pageParameters}
          columns={[
            { Header: t('lunaStreamsGroups:таблица.колонки.group_name'), accessor: 'group_name' },
            { Header: t('lunaStreamsGroups:таблица.колонки.group_id'), accessor: 'group_id' },
            { Header: t('lunaStreamsGroups:таблица.колонки.description'), accessor: 'description' },
            { Header: t('lunaStreamsGroups:таблица.колонки.create_time'), accessor: 'create_time', Cell: TableCells.DateCell },
            { Header: t('lunaStreamsGroups:таблица.колонки.streams'),
              accessor: ({ group_name, group_id, streams }) => ({
                group_id,
                group_name,
                streams,
                refetch,
              }),
              Cell: GroupStreamsCell },
            { Header: t('lunaStreamsGroups:таблица.колонки.account_id'), accessor: 'account_id' },
            actionColumnProps({ id: 'action2', Cell: TableCells.EditCell }),
            actionColumnProps({ id: 'action3', Cell: TableCells.DeleteCell }),
          ]}
          onActions={{
            onEditRow: { handler: onEditRowHandler },
            onDeleteRow: { handler: onDeleteRowHandler },
          }}
          paginationType="offsetBased"
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
      </Panel>
    </Page>
  );
}

GroupListPage.propTypes = {
  deleteGroup: PropTypes.func.isRequired,
  fetchGroups: PropTypes.func.isRequired,
  ...pt.pageData.propTypes,
  ...pt.pageParameters.propTypes,
};

GroupListPage.defaultProps = {
  ...pt.pageData.defaultProps,
  ...pt.pageParameters.defaultProps,
};

export default connect((state) => ({
  groups: selectGroups(state),
  pageData: selectPageData(state),
  pageParameters: selectPageParameters(state),
}), (dispatch) => ({
  setPageIndex: (pageIndex) => dispatch(store.setPageIndex(pageIndex)),
  setPageSize: (pageSize) => dispatch(store.setPageSize(pageSize)),
  deleteGroup: (groupId) => dispatch(store.deleteGroup(groupId)),
  fetchGroups: () => dispatch(store.fetchGroups),
  refetch: () => dispatch(store.fetchPageData),
}))(GroupListPage);
